import React, { useState, useEffect } from 'react';
import { Settings, Save, Shield, AlertCircle, RefreshCw, CheckCircle, AlertTriangle, Globe, Key, Server, Hash } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './ProxySettings.css';

const ProxySettings = () => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [formData, setFormData] = useState({
        proxy_enabled: false,
        soax_package_key: '',
        soax_password: '',
        soax_endpoint: 'proxy.soax.com',
        soax_port: '5000',
        rucaptcha_key: ''
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await fetch('/api/admin/settings', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch settings');
            }

            const data = await response.json();
            setFormData({
                proxy_enabled: data.proxy_enabled,
                soax_package_key: data.soax_package_key || '',
                soax_password: data.soax_password || '',
                soax_endpoint: data.soax_endpoint || 'proxy.soax.com',
                soax_port: data.soax_port || '5000',
                rucaptcha_key: data.rucaptcha_key || ''
            });
        } catch (error) {
            console.error('Error loading settings:', error);
            setStatus({ type: 'error', message: 'Ошибка при загрузке настроек. Пожалуйста, попробуйте снова.' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus({ type: '', message: '' });

        try {
            const response = await fetch('/api/admin/settings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error('Failed to save settings');
            }

            const updatedData = await response.json();
            setFormData({
                proxy_enabled: updatedData.proxy_enabled,
                soax_package_key: updatedData.soax_package_key || '',
                soax_password: updatedData.soax_password || '',
                soax_endpoint: updatedData.soax_endpoint || 'proxy.soax.com',
                soax_port: updatedData.soax_port || '5000',
                rucaptcha_key: updatedData.rucaptcha_key || ''
            });

            setStatus({ type: 'success', message: 'Настройки успешно сохранены!' });

            // Clear success message after 3 seconds
            setTimeout(() => {
                setStatus({ type: '', message: '' });
            }, 3000);

        } catch (error) {
            console.error('Error saving settings:', error);
            setStatus({ type: 'error', message: 'Ошибка при сохранении. Пожалуйста, попробуйте снова.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="proxy-settings-loading">Загрузка настроек прокси...</div>;
    }

    return (
        <div className="proxy-settings-page">
            <div className="page-header">
                <h1>
                    <Shield size={28} />
                    <span>Настройки прокси</span>
                </h1>
                <button className="refresh-btn" onClick={fetchSettings} title="Обновить настройки">
                    <RefreshCw size={20} />
                </button>
            </div>

            {status.type === 'error' && (
                <div className="error-banner">
                    <AlertTriangle size={24} />
                    <span>{status.message}</span>
                </div>
            )}

            <div className="status-grid">
                {/* Proxy Status Card */}
                <div className={`status-card ${formData.proxy_enabled ? 'success' : 'warning'}`}>
                    <div className="card-icon">
                        <Globe size={32} />
                    </div>
                    <div className="card-info">
                        <h3>Статус прокси SOAX</h3>
                        <div className="status-badge">
                            {formData.proxy_enabled ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                            <span>{formData.proxy_enabled ? 'Включено' : 'Выключено'}</span>
                        </div>
                    </div>
                </div>

                {/* Connection Info Card */}
                <div className={`status-card ${formData.proxy_enabled && formData.soax_package_key ? 'success' : ''}`}>
                    <div className="card-icon">
                        <Server size={32} />
                    </div>
                    <div className="card-info">
                        <h3>Подключение</h3>
                        <div className="status-badge">
                            <span>{formData.soax_endpoint}:{formData.soax_port}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="settings-section">
                <h2>Основные настройки</h2>
                <form onSubmit={handleSubmit}>
                    <div className="settings-card">
                        <div className="toggle-row">
                            <div className="toggle-info">
                                <h3>Включить прокси (SOAX)</h3>
                                <p>Использовать сеть SOAX для парсинга. Если выключено, настройки региона будут игнорироваться.</p>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    name="proxy_enabled"
                                    checked={formData.proxy_enabled}
                                    onChange={handleChange}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                    </div>

                    <div className="settings-card credentials-card">
                        <h3>Учётные данные SOAX</h3>
                        <p className="card-description">Введите данные для подключения к прокси-сервису SOAX</p>

                        <div className="form-grid">
                            <div className="form-field">
                                <label htmlFor="soax_package_key">
                                    <Key size={16} />
                                    Ключ пакета (Package Key)
                                </label>
                                <input
                                    type="text"
                                    id="soax_package_key"
                                    name="soax_package_key"
                                    value={formData.soax_package_key}
                                    onChange={handleChange}
                                    placeholder="например, 327745"
                                    disabled={!formData.proxy_enabled}
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="soax_password">
                                    <Key size={16} />
                                    Пароль (Proxy Password)
                                </label>
                                <input
                                    type="password"
                                    id="soax_password"
                                    name="soax_password"
                                    value={formData.soax_password}
                                    onChange={handleChange}
                                    placeholder="Введите пароль прокси..."
                                    disabled={!formData.proxy_enabled}
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="soax_endpoint">
                                    <Globe size={16} />
                                    Адрес прокси (Endpoint)
                                </label>
                                <input
                                    type="text"
                                    id="soax_endpoint"
                                    name="soax_endpoint"
                                    value={formData.soax_endpoint}
                                    onChange={handleChange}
                                    placeholder="proxy.soax.com"
                                    disabled={!formData.proxy_enabled}
                                />
                            </div>

                            <div className="form-field">
                                <label htmlFor="soax_port">
                                    <Hash size={16} />
                                    Порт (Port)
                                </label>
                                <input
                                    type="text"
                                    id="soax_port"
                                    name="soax_port"
                                    value={formData.soax_port}
                                    onChange={handleChange}
                                    placeholder="5000"
                                    disabled={!formData.proxy_enabled}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="settings-card credentials-card">
                        <h3>Решение капч (RuCaptcha / 2captcha)</h3>
                        <p className="card-description">
                            API-ключ сервиса решения капч. Нужен для прохождения силуэтных
                            и слайдер-капч Яндекса. Не забудьте про положительный баланс на rucaptcha.com.
                        </p>

                        <div className="form-grid">
                            <div className="form-field">
                                <label htmlFor="rucaptcha_key">
                                    <Key size={16} />
                                    Ключ RuCaptcha
                                </label>
                                <input
                                    type="password"
                                    id="rucaptcha_key"
                                    name="rucaptcha_key"
                                    value={formData.rucaptcha_key}
                                    onChange={handleChange}
                                    placeholder="Введите API-ключ RuCaptcha..."
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    </div>

                    {!formData.proxy_enabled && (
                        <div className="warning-banner">
                            <AlertCircle size={20} />
                            <div>
                                <strong>Внимание:</strong> Когда прокси выключен, система использует прямой IP адрес сервера.
                                Региональные настройки (например, поиск из "Казани") будут игнорироваться.
                            </div>
                        </div>
                    )}

                    {status.type === 'success' && (
                        <div className="success-banner">
                            <CheckCircle size={20} />
                            <span>{status.message}</span>
                        </div>
                    )}

                    <div className="actions-row">
                        <button type="submit" className="save-btn" disabled={saving}>
                            {saving ? (
                                <>
                                    <RefreshCw className="spin" size={18} />
                                    <span>Сохранение...</span>
                                </>
                            ) : (
                                <>
                                    <Save size={18} />
                                    <span>Сохранить настройки</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            <div className="info-section">
                <h2>Информация о прокси</h2>
                <div className="info-grid">
                    <div className="info-item">
                        <span className="label">Провайдер:</span>
                        <span className="value">SOAX</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Тип:</span>
                        <span className="value">Rotating Residential</span>
                    </div>
                    <div className="info-item">
                        <span className="label">Статус:</span>
                        <span className="value">{formData.proxy_enabled ? 'Активен' : 'Неактивен'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProxySettings;
